<template>
    <div>
        <app-bar :title="t('app_contents_title')"></app-bar>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <card-collection
                :meta="meta"
                :busy="overlay"
                @updated="updated"
            >
                <template #filters>
                    <v-toolbar
                        elevation="0"
                        color="transparent"
                        class="mt-7"
                        height="56"
                    >
                        <v-text-field
                            v-model="queries.search"
                            @click:clear="clear"
                            class="input-search md mr-2"
                            clearable
                            outlined
                            dense
                            hide-details
                            prepend-inner-icon="mdi-magnify"
                            :label="t('general_label_search')"
                        ></v-text-field>

                        <v-select
                            v-model="queries.type"
                            :items="[ { text: '-', value: '' } ].concat(appContents())"
                            class="md mr-2"
                            outlined
                            dense
                            append-icon="mdi-chevron-down"
                            hide-details
                            :label="t('app_content_label_type')"
                        ></v-select>

                        <v-btn
                            @click="search"
                            class="sm mx-5"
                            color="primary"
                        >{{ t('general_action_search') }}</v-btn>

                        <v-spacer></v-spacer>

                        <v-btn
                            v-ripple="false"
                            plain
                            color="orange darken-4"
                            @click="reset"
                        >{{ t('general_action_reset_filters') }}</v-btn>

                    </v-toolbar>
                </template>

                <template #data>
                    <thead>
                    <tr>
                        <th class="touched">
                            <sort-field
                                v-model="queries.sort"
                                field="id"
                                @touched="onSort"
                            >{{ t('app_content_label_id') }}</sort-field>
                        </th>
                        <th>
                            <sort-field
                                v-model="queries.sort"
                                field="name"
                                @touched="onSort"
                            >{{ t('app_content_label_name') }}</sort-field>
                        </th>
                        <th>
                            <sort-field
                                v-model="queries.sort"
                                field="type"
                                @touched="onSort"
                            >{{ t('app_content_label_type') }}</sort-field>
                        </th>
                        <th>{{ t('app_content_label_section') }}</th>
                        <th>{{ t('app_content_label_tags') }}</th>
                        <th>{{ t('app_content_label_published') }}</th>
                        <th class="text-center">{{ t('general_label_action') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="collection.data.length === 0">
                        <td class="text-center" colspan="7">{{ t('general_text_no_data') }}</td>
                    </tr>
                    <template v-else v-for="content in collection.data">
                        <tr>
                            <td><a @click.prevent="touch(content)">{{ content.id }}</a></td>
                            <td><a @click.prevent="touch(content)">{{ content.name }}</a></td>
                            <td>{{ appContentTitle(content.type) }}</td>
                            <td>{{ content.sectionName || '-' }}</td>
                            <td>{{ content.tags ? content.tags.join(', ') : '-' }}</td>
                            <td>{{ content.published ? t('general_text_yes') : t('general_text_no') }}</td>
                            <td class="text-center">
                                <v-menu
                                    offset-y
                                    left
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon>mdi-dots-vertical</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense>
                                        <v-list-item @click.prevent="touch(content)">
                                            <v-list-item-title
                                                class="customized-deep-primary--text"
                                            >{{ t('general_action_edit') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                        <add-tags-modal :value="content" @touched="capture" @cancel="capture">
                                            <template #default="{ on, attrs }">
                                                <v-list-item
                                                    v-on="on"
                                                    v-bind="attrs"
                                                >
                                                    <v-list-item-title
                                                        class="customized-deep-primary--text"
                                                    >{{ t('app_content_title_update_tags') }}
                                                    </v-list-item-title>
                                                </v-list-item>
                                            </template>
                                        </add-tags-modal>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                    </tbody>
                </template>
            </card-collection>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { defineComponent, reactive, computed, onMounted, onUnmounted, toRefs, watch } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useEnums } from '@/core/defaults'
    import { useLocales } from '@/core/defaults'
    import { useRouter } from '@/composition-api'
    import { AppContent } from '@/core/models/app-content'
    import { AppContentCollection } from '@/store/modules/app-content-collection'
    import CardCollection from '@/components/CardCollection.vue'
    import SortField from '@/components/SortField.vue'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import AddTagsModal from '@/components/AddTagsModal.vue'

    export default defineComponent({
        components: {
            ConfirmModal,
            CardCollection,
            SortField,
            AddTagsModal,
        },

        /**
         * @param props
         */
        setup(props) {
            const state = reactive({
                overlay: true,
                meta: undefined
            })

            const queries = reactive({
                search: undefined,
                page: 1,
                per_page: 20,
                sort: '-created_at',
                type: undefined,
            })

            const reset = () => {
                queries.search = undefined
                queries.page = 1
                queries.per_page = 20
                queries.sort = '-id'
                queries.type = undefined
                capture()
            }

            const store = useStore()
            const router = useRouter()

            const collection = computed(() => <AppContentCollection>store.getters['appContentCollection/get'])

            const updated = (updatedQueries: any) => {
                queries.page = updatedQueries.page
                capture()
            }

            const capture = () => {
                state.overlay = true
                store.dispatch('appContentCollection/get', queries).then((data) => {
                    state.meta = data.meta
                    state.overlay = false
                })
            }

            const search = () => {
                capture()
            }

            const clear = () => {
                queries.search = undefined
                capture()
            }

            const onSort = (value: any) => {
                if (value === undefined) {
                    value = '-id'
                }
                queries.sort = value
            }

            const touch = (content: AppContent) => {
                const routeName = {
                    'advice': 'advices.view',
                    'definition': 'glossary-definitions.view',
                    'article': 'health-articles.view'
                }[content.type]
                if (routeName) {
                    window.open(router.resolve({ name: routeName, params: { id: content.id } }).href, '_blank')
                }
            }

            const handleVisibilityChange = () => {
                if (! document.hidden) {
                    capture()
                }
            }

            watch(() => [
                queries.sort,
                queries.type
            ], () => {
                capture()
            })

            onMounted(() => {
                capture()
                document.addEventListener('visibilitychange', handleVisibilityChange)
            })

            onUnmounted(() => {
                document.removeEventListener('visibilitychange', handleVisibilityChange)
            })

            return {
                queries,
                collection: collection.value,
                capture,
                updated,
                onSort,
                search,
                clear,
                reset,
                touch,
                ...useI18n(),
                ...useEnums(),
                ...useLocales(),
                ...toRefs(state)
            }
        }
    })
</script>
